import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import {
  MatLegacyDialog,
  MatLegacyDialogRef
} from '@angular/material/legacy-dialog';
import { InfoPopupComponent } from '../info-popup/info-popup.component';
import {
  DialogStateService,
  DialogState,
  IDialogState
} from '../../services/dialog-state.service';
import { ComponentRendering, Field } from '@sitecore-jss/sitecore-jss-angular';
import { Subscription } from 'rxjs/internal/Subscription';
import { ApprovalPopupComponent } from '../approval-popup/approval-popup.component';
import { RadioButtonsPopupComponent } from '../radio-buttons-popup/radio-buttons-popup.component';
import { DetailsConfirmPopupComponent } from '../details-confirm-popup/details-confirm-popup.component';
import { Router, RouterEvent, NavigationStart } from '@angular/router';
import { filter } from 'rxjs/operators';
import { CountryListPopupComponent } from '../country-list-popup/country-list-popup.component';
import { AddressBookPopupComponent } from '../address-book-popup/address-book-popup.component';
import { PersonalArchiveMessageComponent } from '../personal-archive-message/personal-archive-message.component';
import { UploadDocumentComponent } from '../upload-document/upload-document.component';
import { ErrorHandlingPopupComponent } from '../error-handling-popup/error-handling-popup.component';

@Component({
  selector: 'ab-popup',
  templateUrl: './popup.component.html',
  styleUrls: ['./popup.component.scss']
})
export class PopupComponent implements OnInit, OnDestroy {
  @Input() rendering: ComponentRendering;

  private dialogId: string;
  private dialogStateSubscription: Subscription;
  private routerSubscription: Subscription;
  private matDialogRef: MatLegacyDialogRef<any>;

  constructor(
    private dialog: MatLegacyDialog,
    private dialogStateService: DialogStateService,
    private router: Router
  ) {}

  ngOnInit() {
    this.dialogId = (<any>this.rendering.fields._id).value;
    this.dialogStateService.addDialogId(this.dialogId);

    this.routerSubscription = this.router.events
      .pipe(filter((event: RouterEvent) => event instanceof NavigationStart))
      .subscribe(() => {
        if (this.matDialogRef) {
          this.matDialogRef.close();
        }
      });

    this.dialogStateSubscription = this.dialogStateService
      .getDialogState()
      .subscribe((value: IDialogState) => {
        if (this.dialogId === value.rendaringName) {
          switch (value.dialogState) {
            case DialogState.Open:
              this.openDialog(value.data, value.disableClose);
              break;
            case DialogState.Close:
              this.closeDialog();
              break;
            default:
              console.error('Missing state');
          }
        }
      });
  }

  ngOnDestroy() {
    if (this.dialogStateSubscription) {
      this.dialogStateSubscription.unsubscribe();
    }

    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  private openDialog(data?, disableClose?: boolean) {
    let component;
    let dialogData: any = this.rendering.fields;

    switch ((this.rendering.fields.type as Field).value) {
      case 'Approval':
        component = ApprovalPopupComponent;
        dialogData = {
          approvalData: data,
          fields: dialogData
        };
        break;
      case 'RadioButtons':
        component = RadioButtonsPopupComponent;
        break;
      case 'DetailsConfirm':
        component = DetailsConfirmPopupComponent;
        dialogData = {
          payment: data.details || data,
          labels: data.labels,
          fields: dialogData
        };
        break;
      case 'CountryList':
        component = CountryListPopupComponent;
        dialogData = {
          data: data,
          fields: dialogData
        };
        break;
      case 'AddressBook':
        component = AddressBookPopupComponent;
        dialogData = {
          data: data,
          fields: dialogData
        };
        break;
      case 'PersonalArchive':
        component = PersonalArchiveMessageComponent;
        dialogData = {
          data: data,
          fields: dialogData
        };
        break;
      case 'UploadDocument':
        component = UploadDocumentComponent;
        dialogData = {
          data: data,
          fields: dialogData
        };
        break;
      case 'ErrorHandling':
        component = ErrorHandlingPopupComponent;
        dialogData = {
          data: data,
          fields: dialogData
        };
        break;
      case 'Info':
      default:
        component = InfoPopupComponent;
        break;
    }

    this.matDialogRef = this.dialog.open(component, {
      data: dialogData,
      disableClose:
        (!(this.rendering.fields.HasCloseButtonInHeader as Field)
          .value as boolean) || disableClose,
      panelClass: 'popup-dialog',
      closeOnNavigation: true
    });

    this.matDialogRef.afterClosed().subscribe((dialogCloseValue) => {
      if (!dialogCloseValue) {
        this.dialogStateService.closeDialogByReference(this.dialogId, false);
        return;
      }

      if (
        dialogCloseValue.dialogState &&
        dialogCloseValue.dialogState === DialogState.Dismiss
      ) {
        this.dialogStateService.closeDialogByReference(this.dialogId);
        return;
      }

      this.dialogStateService.closeDialogByReference(
        this.dialogId,
        dialogCloseValue
      );
    });

    if (
      (this.rendering.fields.HasCloseButtonInHeader as Field).value as boolean
    ) {
      this.matDialogRef.backdropClick().subscribe(() => {
        this.dialogStateService.closeDialogByReference(this.dialogId);
      });
    }
  }

  private closeDialog() {
    if (this.matDialogRef) {
      this.matDialogRef.close();
      this.matDialogRef = undefined;
    }
  }
}
